import { secondsToTimeString } from '../utils/secondsToTimeString';

export type BookingUpdateOfferTierThresholdSecondsInput = {
  minTotalExpansionSeconds: number;
  minExpansionEarlierSeconds?: number | null;
  minExpansionLaterSeconds?: number | null;
};

/** Order tiers from smallest required stretch to largest (stable when total minutes is 0). */
export const compareBookingUpdateDiscountTierThresholds = (
  a: BookingUpdateOfferTierThresholdSecondsInput,
  b: BookingUpdateOfferTierThresholdSecondsInput
): number => {
  if (a.minTotalExpansionSeconds !== b.minTotalExpansionSeconds) {
    return a.minTotalExpansionSeconds - b.minTotalExpansionSeconds;
  }
  const aEarlier = a.minExpansionEarlierSeconds ?? 0;
  const bEarlier = b.minExpansionEarlierSeconds ?? 0;
  if (aEarlier !== bEarlier) {
    return aEarlier - bEarlier;
  }
  return (a.minExpansionLaterSeconds ?? 0) - (b.minExpansionLaterSeconds ?? 0);
};

const expansionPart = (seconds: number, suffix: 'total' | 'earlier' | 'later'): string | null => {
  if (seconds <= 0) {
    return null;
  }
  const duration = secondsToTimeString({ seconds, alwaysIncludeSeconds: false }).trim();
  if (suffix === 'later') {
    return `${duration} or later`;
  }
  if (suffix === 'earlier') {
    return `${duration} earlier or more`;
  }
  return `${duration} or more in total`;
};

/** Human-readable discount tier thresholds (e.g. customer offer and partner preview). */
export const formatBookingUpdateOfferTierThresholdSummary = (
  tier: BookingUpdateOfferTierThresholdSecondsInput
): string => {
  const parts: string[] = [];
  const total = expansionPart(tier.minTotalExpansionSeconds, 'total');
  if (total != null) {
    parts.push(total);
  }
  if (tier.minExpansionEarlierSeconds != null && tier.minExpansionEarlierSeconds > 0) {
    const earlier = expansionPart(tier.minExpansionEarlierSeconds, 'earlier');
    if (earlier != null) {
      parts.push(earlier);
    }
  }
  if (tier.minExpansionLaterSeconds != null && tier.minExpansionLaterSeconds > 0) {
    const later = expansionPart(tier.minExpansionLaterSeconds, 'later');
    if (later != null) {
      parts.push(later);
    }
  }
  return parts.length > 0 ? parts.join(', ') : '—';
};
